/**
 * Wallet creation flow orchestrator component.
 *
 * Renders the appropriate step UI based on useCreateWallet state machine:
 * generating -> pin_setup -> encrypting -> biometric_offer -> finalizing -> complete.
 *
 * Composition-only: delegates all logic to useCreateWallet hook.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module OnboardingFlow
 */

import { useEffect, useRef } from 'react';
import { PinSetup } from '@/components/ui/pin-setup';
import { BiometricSetup } from '@/features/onboarding/components/BiometricSetup';
import { OnboardingProgressBar } from '@/features/onboarding/components/OnboardingProgressBar';
import { useCreateWallet } from '@/features/onboarding/hooks/useCreateWallet';

/**
 * Wallet creation flow component.
 *
 * Automatically starts wallet creation on mount.
 * Renders PIN setup, then biometric enrollment offer, then completes.
 *
 * Tier: All tiers (core infrastructure).
 */
export function OnboardingFlow(): React.ReactElement {
  const { state, error, startCreation, setPinAndFinalize, skipBiometric, enableBiometric } =
    useCreateWallet();
  const pinRef = useRef<string | null>(null);

  // Auto-start wallet creation on mount
  useEffect(() => {
    if (state === 'idle') {
      void startCreation();
    }
  }, [state, startCreation]);

  /** Total steps in the create wallet flow visible to the user. */
  const CREATE_TOTAL_STEPS = 3;

  if (state === 'idle' || state === 'generating') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-md">
          <OnboardingProgressBar currentStep={1} totalSteps={CREATE_TOTAL_STEPS} stepName="Creating wallet" />
          <div className="flex flex-col items-center gap-4">
            <div
              className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary"
              aria-hidden="true"
            />
            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              Creating your wallet...
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (state === 'pin_setup') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-md">
          <OnboardingProgressBar currentStep={2} totalSteps={CREATE_TOTAL_STEPS} stepName="Set up PIN" />
          <PinSetup
            onComplete={(pin) => {
              pinRef.current = pin;
              void setPinAndFinalize(pin);
            }}
          />
          {error && (
            <p className="mt-4 text-center text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
      </main>
    );
  }

  if (state === 'biometric_offer') {
    const pin = pinRef.current ?? '';
    return (
      <BiometricSetup
        pin={pin}
        onComplete={() => void enableBiometric(pin)}
        onSkip={skipBiometric}
      />
    );
  }

  if (state === 'encrypting' || state === 'finalizing') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-md">
          <OnboardingProgressBar currentStep={3} totalSteps={CREATE_TOTAL_STEPS} stepName="Securing wallet" />
          <div className="flex flex-col items-center gap-4">
            <div
              className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary"
              aria-hidden="true"
            />
            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              Securing your wallet...
            </p>
          </div>
        </div>
      </main>
    );
  }

  // state === 'complete' -- AuthProvider transitions to unlocked, AuthGate renders children
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-background px-6">
      <div className="w-full max-w-md">
        <OnboardingProgressBar currentStep={CREATE_TOTAL_STEPS} totalSteps={CREATE_TOTAL_STEPS} stepName="Ready" />
        <p className="text-center text-sm text-muted-foreground" role="status" aria-live="polite">
          Wallet ready!
        </p>
      </div>
    </main>
  );
}
